/**
 * Minimal loading / error chrome for the funnel SPA.
 *
 * Renders inside the same outer layout as `<Funnel>` so the page does not
 * "pop" when the resolved config arrives. Brand info is intentionally
 * generic — `useApplicationContext` may not have resolved yet, and showing
 * a brand-mismatched logo is worse than showing none.
 */
import type { ReactElement } from "react";

const Layout = ({ children }: { children: ReactElement }): ReactElement => (
  <div
    className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-4 px-6 text-center"
    role="status"
    aria-live="polite"
  >
    {children}
  </div>
);

export const FunnelLoadingShell = (): ReactElement => (
  <Layout>
    <div className="text-foreground/70 text-sm">Preparing your experience…</div>
  </Layout>
);

export const FunnelErrorShell = ({ message }: { message: string }): ReactElement => (
  <Layout>
    <div className="flex flex-col gap-2">
      <h1 className="text-foreground text-lg font-semibold">We couldn&apos;t load this funnel.</h1>
      <p className="text-foreground/70 text-sm">
        Try refreshing the page. If the issue persists, contact support.
      </p>
      <p className="text-foreground/40 mt-2 text-xs" aria-label="Diagnostic code">
        Reference: {message}
      </p>
    </div>
  </Layout>
);
